import { Card, Center, Divider, LoadingOverlay, SimpleGrid, Skeleton, Stack, Text } from '@mantine/core'
import { IconCheck, IconRefresh, IconX } from '@tabler/icons-react'
import axios from 'axios'
import { Button } from 'components/Button'
import { Layout } from 'components/Layout'
import { NotificationProvider, notifications, modals } from 'context'
import { AvatarOverrideProvider, useAvatarOverride } from 'context/useAvatarOverride'
import { CurrentUserContextProvider, useCurrentUser } from 'context/useCurrentUser'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChangePasswordSection } from './components/ChangePasswordSection'
import { LinkedAccountsSection } from './components/LinkedAccountsSection'
import { MyProfileHeader } from './components/MyProfileHeader'
import { PersonalInfoSection } from './components/PersonalInfoSection'
import { useAvatarUpload } from './hooks/useAvatarUpload'
import { useChangePassword } from './hooks/useChangePassword'
import { useProfileForm } from './hooks/useProfileForm'
import { disconnectGoogle } from './service/profileService'

const RELOAD_DELAY_MS = 1500
const NOTIFICATION_ICON_SIZE = 18

/** 1x1 transparent PNG used to "remove" an avatar via the existing upload endpoint */
const TRANSPARENT_PIXEL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='

function showNotification(message: string, success: boolean) {
  notifications.show({
    message,
    color: success ? 'green' : 'red',
    icon: success ? <IconCheck size={NOTIFICATION_ICON_SIZE} /> : <IconX size={NOTIFICATION_ICON_SIZE} />,
  })
}

function ProfileSkeleton() {
  return (
    <Layout>
      <Stack spacing='md'>
        <MyProfileHeader />
        <Card withBorder radius='sm' p='lg'>
          <Stack spacing='lg'>
            <Skeleton height={20} width={120} />
            <Center><Skeleton height={120} circle /></Center>
            <SimpleGrid cols={2}>
              <Skeleton height={36} />
              <Skeleton height={36} />
            </SimpleGrid>
            <SimpleGrid cols={2}>
              <Skeleton height={36} />
              <Skeleton height={36} />
            </SimpleGrid>
            <Divider />
            <Skeleton height={20} width={150} />
            <Skeleton height={24} width={200} />
            <Divider />
            <Skeleton height={20} width={150} />
            <Skeleton height={36} />
            <SimpleGrid cols={2}>
              <Skeleton height={36} />
              <Skeleton height={36} />
            </SimpleGrid>
          </Stack>
        </Card>
      </Stack>
    </Layout>
  )
}

function MyProfileView() {
  const { t } = useTranslation()
  const { getData: refreshUser } = useCurrentUser()
  const { setAvatarOverride } = useAvatarOverride()

  const {
    form, errors, loading, loadError, saving,
    isEmailEditable, avatarUrl, currentProfile, googleConnected,
    updateField, setAvatarUrl, handleSave,
  } = useProfileForm()

  const { uploading, handleFileSelect: uploadAvatarFile, handleBase64Upload } = useAvatarUpload()

  const {
    form: passwordForm, errors: passwordErrors, saving: passwordSaving,
    updateField: updatePasswordField, handleSave: handlePasswordSave,
  } = useChangePassword()

  useEffect(() => {
    if (loadError) showNotification(t('my_profile.load_failed'), false)
  }, [loadError, t])

  async function handleFileSelect(file: File) {
    const result = await uploadAvatarFile(file)
    if (result.success && result.avatarUrl) {
      setAvatarUrl(result.avatarUrl)
      setAvatarOverride(result.avatarUrl)
      showNotification(t('my_profile.avatar_updated'), true)
    } else {
      showNotification(result.error || t('my_profile.avatar_update_failed'), false)
    }
  }

  async function handleBase64Capture(base64: string) {
    const result = await handleBase64Upload(base64)
    if (result.success && result.avatarUrl) {
      setAvatarUrl(result.avatarUrl)
      setAvatarOverride(result.avatarUrl)
      showNotification(t('my_profile.avatar_updated'), true)
    } else {
      showNotification(result.error || t('my_profile.avatar_update_failed'), false)
    }
  }

  async function executeRemoveAvatar() {
    const result = await handleBase64Upload(TRANSPARENT_PIXEL)
    if (result.success) {
      setAvatarUrl('')
      setAvatarOverride('')
      showNotification(t('my_profile.avatar_removed'), true)
    } else {
      showNotification(result.error || t('my_profile.avatar_update_failed'), false)
    }
  }

  function handleRemoveAvatar() {
    modals.openConfirmModal({
      title: t('my_profile.remove_avatar_confirm_title'),
      centered: true,
      children: <Text size='sm'>{t('my_profile.remove_avatar_confirm_message')}</Text>,
      labels: { confirm: t('my_profile.remove_avatar'), cancel: t('my_profile.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: executeRemoveAvatar,
    })
  }

  async function handleProfileSave() {
    const success = await handleSave()
    if (!success && errors.general) {
      showNotification(errors.general, false)
    } else {
      showNotification(
        success ? t('my_profile.profile_updated') : t('my_profile.profile_update_failed'),
        success,
      )
    }
  }

  const [disconnecting, setDisconnecting] = useState(false)

  async function executeDisconnect() {
    setDisconnecting(true)
    try {
      await disconnectGoogle()
      showNotification(t('my_profile.disconnect_success'), true)
      // Log out and redirect to password recovery so the user can set a new password
      setTimeout(async () => {
        try {
          await axios.delete('/users/sign_out')
        } catch {
          // session may already be invalidated by the backend disconnect
        }
        window.location.href = '/forgot-password'
      }, RELOAD_DELAY_MS)
    } catch {
      showNotification(t('my_profile.disconnect_failed'), false)
      setDisconnecting(false)
    }
  }

  function handleDisconnectGoogle() {
    modals.openConfirmModal({
      title: t('my_profile.disconnect_confirm_title'),
      centered: true,
      children: <Text size='sm'>{t('my_profile.disconnect_confirm_message')}</Text>,
      labels: { confirm: t('my_profile.disconnect'), cancel: t('my_profile.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: executeDisconnect,
    })
  }

  function handleConnectSuccess() {
    refreshUser()
    setTimeout(() => window.location.reload(), RELOAD_DELAY_MS)
  }

  async function handlePasswordSaveWithToast() {
    const success = await handlePasswordSave()
    if (!success && passwordErrors.general) {
      showNotification(passwordErrors.general, false)
    } else {
      showNotification(
        success ? t('my_profile.password_updated') : t('my_profile.password_update_failed'),
        success,
      )
    }
  }

  if (loading) return <ProfileSkeleton />

  if (loadError) {
    return (
      <Layout>
        <Center py='xl'>
          <Stack align='center' spacing='sm'>
            <Text color='dimmed' size='sm'>{t('my_profile.could_not_load')}</Text>
            <Button
              variant='subtle'
              size='sm'
              leftIcon={<IconRefresh size={16} />}
              onClick={() => window.location.reload()}
            >
              {t('my_profile.reload_page')}
            </Button>
          </Stack>
        </Center>
      </Layout>
    )
  }

  if (disconnecting) {
    return (
      <Layout>
        <Card withBorder radius='sm' p='lg' style={{ position: 'relative', minHeight: 300 }}>
          <LoadingOverlay visible overlayBlur={2} />
        </Card>
      </Layout>
    )
  }

  return (
    <Layout>
      <Stack spacing='md'>
        <MyProfileHeader />
        <Card withBorder radius='sm' p='lg'>
          <Stack spacing='lg'>
            <PersonalInfoSection
              firstName={form.firstName}
              lastName={form.lastName}
              email={form.email}
              username={form.username}
              currentProfile={currentProfile}
              avatarUrl={avatarUrl}
              uploading={uploading}
              saving={saving}
              isEmailEditable={isEmailEditable}
              errors={errors}
              onFieldChange={updateField}
              onFileSelect={handleFileSelect}
              onBase64Capture={handleBase64Capture}
              onRemoveAvatar={handleRemoveAvatar}
              onSave={handleProfileSave}
            />
            <Divider />
            <LinkedAccountsSection
              googleConnected={googleConnected}
              disconnecting={disconnecting}
              onDisconnect={handleDisconnectGoogle}
              onConnectSuccess={handleConnectSuccess}
              userEmail={form.email}
            />
            <Divider />
            <ChangePasswordSection
              currentPassword={passwordForm.currentPassword}
              newPassword={passwordForm.newPassword}
              confirmPassword={passwordForm.confirmPassword}
              saving={passwordSaving}
              errors={passwordErrors}
              onFieldChange={updatePasswordField}
              onSave={handlePasswordSaveWithToast}
            />
          </Stack>
        </Card>
      </Stack>
    </Layout>
  )
}

export function MyProfilePage() {
  return (
    <AvatarOverrideProvider>
      <CurrentUserContextProvider>
        <NotificationProvider>
          <MyProfileView />
        </NotificationProvider>
      </CurrentUserContextProvider>
    </AvatarOverrideProvider>
  )
}
